import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
    Box, Typography, Paper, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, MenuItem, Select, FormControl,
    InputLabel, Grid, Card, Stack, TextField, IconButton, Chip, CircularProgress, Avatar
} from "@mui/material";
import {
    PhotoCamera, History, Delete, Save, Close, CheckCircle, Cancel, BeachAccess, WatchLater
} from "@mui/icons-material";
import { collection, getDocs, doc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import { format, differenceInMinutes, parseISO } from "date-fns";

function Staffattendance() {
    const [staffList, setStaffList] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [attendanceData, setAttendanceData] = useState({});
    const [viewDate, setViewDate] = useState(new Date());
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [previewImg, setPreviewImg] = useState(null);

    // Form States
    const [selectedDate, setSelectedDate] = useState(null);
    const [status, setStatus] = useState("Present");
    const [checkInTime, setCheckInTime] = useState("09:00");
    const [checkOutTime, setCheckOutTime] = useState("18:00");

    // Image Preview and Actual File
    const [checkInImg, setCheckInImg] = useState("");
    const [checkInFile, setCheckInFile] = useState(null);
    const [checkOutImg, setCheckOutImg] = useState("");
    const [checkOutFile, setCheckOutFile] = useState(null);

    // Computed Stats
    const [monthlyStats, setMonthlyStats] = useState({
        present: 0,
        absent: 0,
        halfDay: 0,
        paidHoliday: 0,
        allowedHolidays: 3,
        salaryEarned: 0
    });

    // Map to store calculated statuses for specific dates (e.g., overriding "Absent" to "Paid Holiday")
    const [derivedStatuses, setDerivedStatuses] = useState({});

    useEffect(() => { loadStaff(); loadAttendance(); }, []);

    useEffect(() => {
        if (selectedStaff) calculateMonthlyStats(viewDate);
    }, [attendanceData, selectedStaff, viewDate]);

    const loadStaff = async () => {
        const snapshot = await getDocs(collection(db, "staff"));
        const activeStaff = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            // FETCH ONLY CURRENT WORKING DATA
            .filter(s => s.status === "Currently Working" || !s.status);

        setStaffList(activeStaff);
    };

    const loadAttendance = async () => {
        const snapshot = await getDocs(collection(db, "staffattendance"));
        let map = {};
        snapshot.docs.forEach(d => {
            const data = d.data();
            const key = `${data.staffId}_${data.date}`;
            map[key] = { docId: d.id, ...data };
        });
        setAttendanceData(map);
    };

    const calculateMonthlyStats = (activeDate) => {
        if (!selectedStaff) return;

        const monthStr = format(activeDate, "yyyy-MM");

        // Filter and sort records chronologically to find the FIRST allowed absences
        const records = Object.values(attendanceData)
            .filter(r => r.staffId === selectedStaff.id && r.date.startsWith(monthStr))
            .sort((a, b) => a.date.localeCompare(b.date));

        let p = 0, a = 0, h = 0, ph = 0;

        // FETCH PAID HOLIDAYS FROM STAFF DATA (Default to 3 if missing)
        const allowedHolidays = selectedStaff.paidHolidays !== undefined ? Number(selectedStaff.paidHolidays) : 3;
        const newDerived = {};

        records.forEach(r => {
            let finalStatus = r.status;

            if (r.status === "Absent") {
                if (ph < allowedHolidays) {
                    finalStatus = "Paid Holiday";
                    ph++;
                } else {
                    a++; // Unpaid Absent
                }
            } else if (r.status === "Present") {
                p++;
            } else if (r.status === "Half Day") {
                h++;
            }

            newDerived[r.date] = finalStatus;
        });

        // --- UPDATED SALARY CALCULATION ---
        // 1. Get exact number of days in the selected month (e.g., 28, 30, 31)
        const daysInMonth = new Date(activeDate.getFullYear(), activeDate.getMonth() + 1, 0).getDate();

        const salary = Number(selectedStaff.payment) || 0;
        const perDaySalary = salary / daysInMonth;

        // 2. Deduct ONLY unpaid absences (a) and 50% for half-days (h)
        const totalDeduction = (a * perDaySalary) + (h * 0.5 * perDaySalary);

        // 3. Calculate final earned and prevent negative balances
        let earned = Math.max(0, Math.round(salary - totalDeduction));

        setDerivedStatuses(newDerived);
        setMonthlyStats({
            present: p,
            absent: a,
            halfDay: h,
            paidHoliday: ph,
            allowedHolidays: allowedHolidays,
            salaryEarned: earned
        });
    };

    const handleDateClick = (date) => {
        if (!selectedStaff) return;
        const dStr = format(date, "yyyy-MM-dd");
        const existing = attendanceData[`${selectedStaff.id}_${dStr}`];

        setSelectedDate(date);

        // Always set the form to the actual database status ("Absent" not "Paid Holiday")
        setStatus(existing?.status || "Present");

        setCheckInImg(existing?.checkInImage || "");
        setCheckOutImg(existing?.checkOutImage || "");
        setCheckInFile(null);
        setCheckOutFile(null);

        setCheckInTime(existing?.checkInTime ? format(new Date(existing.checkInTime), "HH:mm") : "09:00");
        setCheckOutTime(existing?.checkOutTime ? format(new Date(existing.checkOutTime), "HH:mm") : "18:00");

        setOpen(true);
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            if (type === 'in') setCheckInFile(file);
            else setCheckOutFile(file);

            const reader = new FileReader();
            reader.onloadend = () => {
                if (type === 'in') setCheckInImg(reader.result);
                else setCheckOutImg(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        if (!selectedDate || !selectedStaff) return;
        setLoading(true);

        try {
            const dStr = format(selectedDate, "yyyy-MM-dd");
            const docId = `${selectedStaff.id}_${dStr}`;
            const docRef = doc(db, "staffattendance", docId);

            const getFullISO = (timeStr) => {
                const [hours, mins] = timeStr.split(':');
                const d = new Date(selectedDate);
                d.setHours(parseInt(hours), parseInt(mins), 0, 0);
                return d.toISOString();
            };

            let finalCheckInUrl = checkInImg;
            let finalCheckOutUrl = checkOutImg;

            if (checkInFile) {
                const refIn = ref(storage, `attendance/att_${selectedStaff.id}_${Date.now()}_in.jpg`);
                await uploadBytes(refIn, checkInFile);
                finalCheckInUrl = await getDownloadURL(refIn);
            }

            if (checkOutFile) {
                const refOut = ref(storage, `attendance/att_${selectedStaff.id}_${Date.now()}_out.jpg`);
                await uploadBytes(refOut, checkOutFile);
                finalCheckOutUrl = await getDownloadURL(refOut);
            }

            const payload = {
                staffId: selectedStaff.id,
                staffName: selectedStaff.name,
                date: dStr,
                status: status, // "Absent" will be saved, "Paid Holiday" is calculated on the fly
                checkInTime: getFullISO(checkInTime),
                checkInImage: finalCheckInUrl,
                checkOutTime: getFullISO(checkOutTime),
                checkOutImage: finalCheckOutUrl,
                checkInDistance: 0,
                checkOutDistance: 0,
                targetRadius: 100,
                manualEntry: true
            };

            await setDoc(docRef, payload, { merge: true });
            await loadAttendance();
            setOpen(false);
        } catch (error) {
            console.error("Save Error:", error);
            alert("Error saving record.");
        }
        setLoading(false);
    };

    const calculateDuration = (inT, outT) => {
        if (!inT || !outT) return "0h 0m";
        const mins = differenceInMinutes(new Date(outT), new Date(inT));
        return `${Math.floor(mins / 60)}h ${mins % 60}m`;
    };

    return (
        <Box>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 5, border: '1px solid #f0f0f0' }}>

                <Grid className="row" spacing={3}>
                    <Grid className="col-sm-4 mb-2">
                        <Stack className="col-sm-12 mb-2" direction="row" justifyContent="space-between" mb={4} alignItems="center">
                            <FormControl size="small" sx={{ width: "100%" }}>
                                <InputLabel>Select Active Staff</InputLabel>
                                <Select
                                    value={selectedStaff?.id || ""}
                                    label="Select Active Staff" fullWidth
                                    onChange={(e) => setSelectedStaff(staffList.find(s => s.id === e.target.value))}
                                >
                                    {staffList.map(s => <MenuItem key={s.id} value={s.id}>{s.name} ({s.staffCode})</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Stack>

                        <Stack spacing={2}>
                            {/* Monthly Earnings Card */}
                            <Card sx={{
                                p: 2,
                                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                color: 'white',
                                borderRadius: 4,
                                textAlign: 'center',
                                boxShadow: '0 4px 12px rgba(21, 101, 192, 0.3)'
                            }}>
                                <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 1 }}>MONTHLY EARNINGS</Typography>
                                <Typography variant="h4" fontWeight="900">₹{monthlyStats.salaryEarned}</Typography>

                                {/* Stats Breakdown */}
                                <Grid container spacing={1} sx={{ mt: 2, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>Present</Typography>
                                        <Typography variant="body2" fontWeight="bold">{monthlyStats.present}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>Half Day</Typography>
                                        <Typography variant="body2" fontWeight="bold">{monthlyStats.halfDay}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>Paid Leave</Typography>
                                        <Typography variant="body2" fontWeight="bold">{monthlyStats.paidHoliday}/{monthlyStats.allowedHolidays}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>Unpaid</Typography>
                                        <Typography variant="body2" fontWeight="bold">{monthlyStats.absent}</Typography>
                                    </Grid>
                                </Grid>
                            </Card>

                            {/* Recent Logs Card */}
                            <Card variant="outlined" sx={{ borderRadius: 4, maxHeight: 480, overflow: 'auto', border: '1px solid #e0e0e0' }}>
                                <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="800" display="flex" alignItems="center" gap={1}>
                                        <History fontSize="small" color="primary" /> Recent Logs
                                    </Typography>
                                </Box>

                                <Box sx={{ p: 1 }}>
                                    {Object.values(attendanceData)
                                        .filter(r => r.staffId === selectedStaff?.id && r.date.startsWith(format(viewDate, "yyyy-MM")))
                                        .sort((a, b) => b.date.localeCompare(a.date))
                                        .map((log, i) => {
                                            const computedStatus = derivedStatuses[log.date] || log.status;

                                            const statusConfig = {
                                                "Present": { color: "success", label: "Present" },
                                                "Absent": { color: "error", label: "Unpaid Absent" },
                                                "Half Day": { color: "warning", label: "Half Day" },
                                                "Paid Holiday": { color: "info", label: "Paid Holiday" }
                                            };
                                            const config = statusConfig[computedStatus] || { color: "default", label: computedStatus };

                                            return (
                                                <Box key={i} sx={{
                                                    mb: 1.5, p: 2, borderRadius: 3, bgcolor: '#f8f9fa', border: '1px solid #f0f0f0',
                                                    transition: '0.2s', '&:hover': { bgcolor: '#f1f3f4', transform: 'translateY(-2px)' }
                                                }}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                                                        <Typography variant="body2" fontWeight="800" sx={{ color: '#37474f' }}>
                                                            {format(parseISO(log.date), "dd MMM, yyyy")}
                                                        </Typography>
                                                        <Chip size="small" label={config.label} color={config.color} sx={{ fontWeight: 'bold', borderRadius: 1.5, fontSize: '0.7rem' }} />
                                                    </Stack>

                                                    {computedStatus !== "Absent" && computedStatus !== "Paid Holiday" && (
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={5}>
                                                                <Typography variant="caption" color="textSecondary" display="block">CHECK IN</Typography>
                                                                <Typography variant="body2" fontWeight="600" color="success.main">
                                                                    {log.checkInTime ? format(new Date(log.checkInTime), "hh:mm a") : "--:--"}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={5}>
                                                                <Typography variant="caption" color="textSecondary" display="block">CHECK OUT</Typography>
                                                                <Typography variant="body2" fontWeight="600" color="error.main">
                                                                    {log.checkOutTime ? format(new Date(log.checkOutTime), "hh:mm a") : "--:--"}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={2} sx={{ textAlign: 'right' }}>
                                                                <Typography variant="caption" color="textSecondary" display="block">TOTAL</Typography>
                                                                <Typography variant="body2" fontWeight="700">
                                                                    {calculateDuration(log.checkInTime, log.checkOutTime)}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    )}

                                                    {/* Image Thumbnails Section */}
                                                    {(log.checkInImage || log.checkOutImage) && (
                                                        <Stack direction="row" spacing={2} mt={2} pt={1} sx={{ borderTop: '1px dashed #ddd' }}>
                                                            {log.checkInImage && (
                                                                <Box onClick={() => setPreviewImg(log.checkInImage)} sx={{ cursor: 'pointer', position: 'relative' }}>
                                                                    <Avatar variant="rounded" src={log.checkInImage} sx={{ width: 45, height: 45, border: '1px solid #ccc' }} />
                                                                    <Typography variant="caption" sx={{ position: 'absolute', bottom: -5, left: '50%', transform: 'translateX(-50%)', bgcolor: 'success.main', color: 'white', px: 0.5, borderRadius: 1, fontSize: '0.6rem', zIndex: 2 }}>IN</Typography>
                                                                </Box>
                                                            )}
                                                            {log.checkOutImage && (
                                                                <Box onClick={() => setPreviewImg(log.checkOutImage)} sx={{ cursor: 'pointer', position: 'relative' }}>
                                                                    <Avatar variant="rounded" src={log.checkOutImage} sx={{ width: 45, height: 45, border: '1px solid #ccc' }} />
                                                                    <Typography variant="caption" sx={{ position: 'absolute', bottom: -5, left: '50%', transform: 'translateX(-50%)', bgcolor: 'error.main', color: 'white', px: 0.5, borderRadius: 1, fontSize: '0.6rem', zIndex: 2 }}>OUT</Typography>
                                                                </Box>
                                                            )}
                                                        </Stack>
                                                    )}
                                                </Box>
                                            );
                                        })
                                    }
                                </Box>
                            </Card>

                            <Dialog open={Boolean(previewImg)} onClose={() => setPreviewImg(null)} maxWidth="sm" fullWidth>
                                <Box sx={{ position: 'relative', p: 1, bgcolor: '#000', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                                    <IconButton
                                        onClick={() => setPreviewImg(null)}
                                        sx={{ position: 'absolute', right: 8, top: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', zIndex: 10, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                                    >
                                        <Close />
                                    </IconButton>
                                    {previewImg && (
                                        <img src={previewImg} alt="Preview" style={{ width: '100%', height: 'auto', borderRadius: '4px', maxHeight: '85vh', objectFit: 'contain' }} />
                                    )}
                                </Box>
                            </Dialog>
                        </Stack>
                    </Grid>

                    <Grid className="col-sm-8 mb-2">
                        {/* Status Legends */}
                        <Stack direction="row" spacing={2} justifyContent="center" mb={2}>
                            <Chip size="small" icon={<CheckCircle fontSize="small" />} label="Present" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold' }} />
                            <Chip size="small" icon={<BeachAccess fontSize="small" />} label="Paid Holiday" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold' }} />
                            <Chip size="small" icon={<Cancel fontSize="small" />} label="Unpaid Absent" sx={{ bgcolor: '#ffebee', color: '#d32f2f', fontWeight: 'bold' }} />
                            <Chip size="small" icon={<WatchLater fontSize="small" />} label="Half Day" sx={{ bgcolor: '#fff3e0', color: '#ed6c02', fontWeight: 'bold' }} />
                        </Stack>

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 4 }}>
                            {selectedStaff ? (
                                <Calendar
                                    onClickDay={handleDateClick}
                                    tileClassName={({ date, view }) => {
                                        if (view !== 'month') return;
                                        const key = format(date, "yyyy-MM-dd");
                                        const compStatus = derivedStatuses[key];

                                        if (compStatus === "Present") return "present-tile";
                                        if (compStatus === "Absent") return "absent-tile";
                                        if (compStatus === "Half Day") return "halfday-tile";
                                        if (compStatus === "Paid Holiday") return "paidholiday-tile";
                                        return "";
                                    }}
                                    onActiveStartDateChange={({ activeStartDate }) => setViewDate(activeStartDate)}
                                />
                            ) : <Typography sx={{ textAlign: 'center', py: 10 }}>Please select active staff to view calendar</Typography>}
                        </Paper>
                    </Grid>
                </Grid>
            </Paper>

            <Dialog open={open} onClose={() => !loading && setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Mark Attendance: {format(selectedDate || new Date(), "dd MMM yyyy")}</DialogTitle>
                <DialogContent dividers>
                    <Grid className="row" spacing={2}>
                        <Grid className="col-sm-12 mb-3">
                            <Stack direction="row" spacing={1}>
                                {["Present", "Absent", "Half Day"].map(s => (
                                    <Button key={s} fullWidth variant={status === s ? "contained" : "outlined"} onClick={() => setStatus(s)}>{s}</Button>
                                ))}
                            </Stack>
                            {status === "Absent" && (
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                    (Will automatically be marked as Paid Leave if within monthly limit of {monthlyStats.allowedHolidays})
                                </Typography>
                            )}
                        </Grid>

                        <Grid className="col-sm-12 mb-3">
                            <TextField label="Check-In" type="time" fullWidth value={checkInTime} onChange={e => setCheckInTime(e.target.value)} InputLabelProps={{ shrink: true }} />
                            <Box sx={{ mt: 1, border: '1px dashed #ccc', height: 120, borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
                                {checkInImg ? (
                                    <>
                                        <img src={checkInImg} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <IconButton size="small" sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'white' }} onClick={() => { setCheckInImg(""); setCheckInFile(null) }}><Delete fontSize="small" color="error" /></IconButton>
                                    </>
                                ) : (
                                    <Button component="label" fullWidth sx={{ height: '100%' }} startIcon={<PhotoCamera />}>
                                        Check-In Photo <input hidden accept="image/*" type="file" onChange={e => handleFileChange(e, 'in')} />
                                    </Button>
                                )}
                            </Box>
                        </Grid>

                        <Grid className="col-sm-12 mb-3">
                            <TextField label="Check-Out" type="time" fullWidth value={checkOutTime} onChange={e => setCheckOutTime(e.target.value)} InputLabelProps={{ shrink: true }} />
                            <Box sx={{ mt: 1, border: '1px dashed #ccc', height: 120, borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
                                {checkOutImg ? (
                                    <>
                                        <img src={checkOutImg} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <IconButton size="small" sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'white' }} onClick={() => { setCheckOutImg(""); setCheckOutFile(null) }}><Delete fontSize="small" color="error" /></IconButton>
                                    </>
                                ) : (
                                    <Button component="label" fullWidth sx={{ height: '100%' }} startIcon={<PhotoCamera />}>
                                        Check-Out Photo <input hidden accept="image/*" type="file" onChange={e => handleFileChange(e, 'out')} />
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpen(false)} disabled={loading}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    >
                        {loading ? "Saving..." : "Save Attendance"}
                    </Button>
                </DialogActions>
            </Dialog>

            <style>{`
                .react-calendar { width: 100% !important; border: none !important; font-family: inherit !important; }
                .react-calendar__month-view__days {
                    display: grid !important;
                    grid-template-columns: repeat(7, 1fr) !important;
                    gap: 4px;
                }
                .react-calendar__tile {
                    max-width: initial !important;
                    height: 75px !important; 
                    border-radius: 8px;
                    margin: 0 !important; /* Reset margin */
                }
                
                /* Custom Tile Colors */
                .present-tile { background: #e8f5e9 !important; color: #2e7d32 !important; border: 1px solid #a5d6a7 !important; font-weight: bold; }
                .absent-tile { background: #ffebee !important; color: #d32f2f !important; border: 1px solid #ef9a9a !important; font-weight: bold; }
                .halfday-tile { background: #fff3e0 !important; color: #ed6c02 !important; border: 1px solid #ffcc80 !important; font-weight: bold; }
                
                /* New Paid Holiday Color (Blue) */
                .paidholiday-tile { background: #e3f2fd !important; color: #1565c0 !important; border: 1px solid #90caf9 !important; font-weight: bold; }
                
                .react-calendar__tile--active { background: #1976d2 !important; color: white !important; }
                .react-calendar__navigation button { font-weight: bold; font-size: 1.1rem; }
            `}</style>
        </Box>
    );
}

export default Staffattendance;